/**
 * HandStrengthMeter for Poker Reborn
 *
 * Vertical bar that shows the hero's current hand strength (1-10).
 * Fills bottom-to-top with a color gradient from red (weak) to green (strong).
 * Positioned to the right of the hero's hole cards.
 *
 * @see docs/screens/room/HAND_STRENGTH_METER.md - Visual specs, behavior, dimensions
 * @see tasks/active/T39_HAND_STRENGTH_METER_IMPL.md - Implementation plan
 * @see tasks/active/HAND_STRENGTH_METER.md - Research & design
 */

import { BlurFilter, Container, FillGradient, Graphics, Text, type TextStyleFontWeight } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';
import { HAND_STRENGTH_METER_SIZE } from '@/config/cockpit';

// =============================================================================
// STYLES
// =============================================================================

const { barWidth: BAR_WIDTH, barHeight: BAR_HEIGHT, cornerRadius: CORNER_RADIUS,
        labelFontMin: LABEL_FONT_MIN, labelFontMax: LABEL_FONT_MAX,
        labelGapX: LABEL_GAP_X, labelMaxWidth: LABEL_MAX_WIDTH } = HAND_STRENGTH_METER_SIZE;

/** Label color: grey (weak) → bar color (strong) */
const LABEL_WEAK_COLOR = 0x888888;

/** Font weight thresholds (mapped to CSS values) */
const WEIGHT_LIGHT_MAX = 3;   // values 1-3: light (300)
const WEIGHT_NORMAL_MAX = 6;  // values 4-6: normal (400)
                               // values 7-10: bold (700)

/** Background bar style */
const BACKGROUND_COLOR = 0x1a1a2e;
const BACKGROUND_ALPHA = 0.8;
const BAR_BORDER_COLOR = 0x333355;
const BAR_BORDER_WIDTH = 1;
const BAR_SHADOW_COLOR = 0x000000;
const BAR_SHADOW_ALPHA = 0.4;
const BAR_SHADOW_OFFSET = 2;
const BAR_SHADOW_BLUR = 3;

/** Color stops for the fill gradient (position 0-1 → color) */
const COLOR_STOPS: [number, number][] = [
  [0.0, 0xff1744],  // Red (weak)
  [0.3, 0xff8f00],  // Orange
  [0.5, 0xffd54f],  // Yellow
  [0.7, 0x66ff44],  // Light green
  [1.0, 0x00e676],  // Green (strong)
];

// =============================================================================
// COMPONENT
// =============================================================================

export class HandStrengthMeter extends Container {
  private barShadow: Graphics;
  private backgroundBar: Graphics;
  private fillBar: Graphics;
  private glossOverlay: Graphics;
  private nameLabel: Text;

  private currentValue = 0;
  private barWidth = 10;
  private barHeight = 70;
  private cornerRadius = 4;
  private baseFontMin = 10;
  private baseFontMax = 16;
  private lastScreenWidth = 1920;

  constructor() {
    super();
    this.label = 'handStrengthMeter';

    this.barShadow = new Graphics();
    this.addChild(this.barShadow);

    this.backgroundBar = new Graphics();
    this.addChild(this.backgroundBar);

    this.fillBar = new Graphics();
    this.addChild(this.fillBar);

    this.glossOverlay = new Graphics();
    this.addChild(this.glossOverlay);

    this.nameLabel = new Text({
      text: '',
      style: {
        fill: LABEL_WEAK_COLOR,
        fontSize: 10,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: '300',
        align: 'left',
        wordWrap: true,
        wordWrapWidth: 70,
        stroke: { color: 0x000000, width: 2 },
        dropShadow: {
          alpha: 0.5,
          angle: Math.PI / 4,
          blur: 2,
          color: 0x000000,
          distance: 1,
        },
      },
    });
    this.nameLabel.anchor.set(0, 0.5);
    this.nameLabel.visible = false;
    this.addChild(this.nameLabel);

    this.visible = false;
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set hand strength value (1-10). Redraws the bar.
   * @param value - Strength value (1-10, or 0 to hide)
   * @param handName - Optional label text to display below the bar
   */
  setValue(value: number, handName?: string): void {
    if (value < 0 || value > 10) return;
    this.currentValue = value;
    this.drawFill();

    if (handName) {
      this.nameLabel.text = handName;
      this.nameLabel.visible = true;
      this.updateLabelStyle();
    }
  }

  /**
   * Reset to empty state (new hand).
   */
  reset(): void {
    this.currentValue = 0;
    this.fillBar.clear();
    this.glossOverlay.clear();
    this.nameLabel.text = '';
    this.nameLabel.visible = false;
  }

  /**
   * Update responsive dimensions based on screen width.
   */
  updateLayout(screenWidth: number): void {
    this.lastScreenWidth = screenWidth;
    this.barWidth = getScaledValue(screenWidth, BAR_WIDTH, { discrete: false });
    this.barHeight = getScaledValue(screenWidth, BAR_HEIGHT, { discrete: false });
    this.cornerRadius = getScaledValue(screenWidth, CORNER_RADIUS, { discrete: false });

    this.baseFontMin = getScaledValue(screenWidth, LABEL_FONT_MIN, { discrete: true });
    this.baseFontMax = getScaledValue(screenWidth, LABEL_FONT_MAX, { discrete: true });

    this.drawBarShadow();
    this.drawBackground();
    if (this.currentValue > 0) {
      this.drawFill();
    }

    // Update label position
    if (this.nameLabel.visible) {
      this.updateLabelStyle();
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /** Update label position, size, weight, and color based on current strength */
  private updateLabelStyle(): void {
    const fillPercent = this.currentValue / 10;
    const gap = getScaledValue(this.lastScreenWidth, LABEL_GAP_X, { discrete: false });

    // Position: vertically aligned with top of fill bar (centered origin)
    const offsetX = -this.barWidth / 2;
    const offsetY = -this.barHeight / 2;
    const fillTop = offsetY + this.barHeight - this.barHeight * fillPercent;
    this.nameLabel.x = offsetX + this.barWidth + gap;
    this.nameLabel.y = fillTop;
    this.nameLabel.anchor.set(0, 0.5);

    // Font size: scales linearly from min (weak) to max (strong)
    const fontSize = Math.round(this.baseFontMin + (this.baseFontMax - this.baseFontMin) * fillPercent);
    this.nameLabel.style.fontSize = fontSize;

    // Word wrap width
    const maxWidth = getScaledValue(this.lastScreenWidth, LABEL_MAX_WIDTH, { discrete: false });
    this.nameLabel.style.wordWrapWidth = maxWidth;

    // Font weight: thin → normal → bold
    let fontWeight: TextStyleFontWeight;
    if (this.currentValue <= WEIGHT_LIGHT_MAX) {
      fontWeight = '300';
    } else if (this.currentValue <= WEIGHT_NORMAL_MAX) {
      fontWeight = '400';
    } else {
      fontWeight = '700';
    }
    this.nameLabel.style.fontWeight = fontWeight;

    // Color: grey (weak) → bar fill color (strong)
    const fillColor = interpolateColor(fillPercent);
    const labelColor = lerpColor(LABEL_WEAK_COLOR, fillColor, fillPercent);
    this.nameLabel.style.fill = labelColor;
  }

  /** Draw the bar shadow (offset dark shape behind the bar) */
  private drawBarShadow(): void {
    const offsetX = -this.barWidth / 2;
    const offsetY = -this.barHeight / 2;
    this.barShadow.clear();
    this.barShadow.roundRect(
      offsetX + BAR_SHADOW_OFFSET,
      offsetY + BAR_SHADOW_OFFSET,
      this.barWidth,
      this.barHeight,
      this.cornerRadius,
    );
    this.barShadow.fill({ color: BAR_SHADOW_COLOR, alpha: BAR_SHADOW_ALPHA });
    this.barShadow.filters = [new BlurFilter({ strength: BAR_SHADOW_BLUR })];
  }

  /** Draw the dark background bar with border (always visible) */
  private drawBackground(): void {
    const offsetX = -this.barWidth / 2;
    const offsetY = -this.barHeight / 2;
    this.backgroundBar.clear();
    this.backgroundBar.roundRect(offsetX, offsetY, this.barWidth, this.barHeight, this.cornerRadius);
    this.backgroundBar.fill({ color: BACKGROUND_COLOR, alpha: BACKGROUND_ALPHA });
    this.backgroundBar.stroke({ color: BAR_BORDER_COLOR, width: BAR_BORDER_WIDTH });
  }

  /** Draw the colored fill bar — thermometer gradient + glossy highlight */
  private drawFill(): void {
    this.fillBar.clear();
    this.glossOverlay.clear();

    if (this.currentValue <= 0) return;

    const offsetX = -this.barWidth / 2;
    const offsetY = -this.barHeight / 2;
    const fillPercent = this.currentValue / 10;
    const fillHeight = this.barHeight * fillPercent;
    const fillY = offsetY + this.barHeight - fillHeight;

    // Thermometer: red at bottom → current strength color at top
    const thermometerGradient = new FillGradient({
      type: 'linear',
      start: { x: 0.5, y: 1 },
      end: { x: 0.5, y: 0 },
      colorStops: buildGradientStops(fillPercent),
      textureSpace: 'local',
    });

    this.fillBar.roundRect(offsetX, fillY, this.barWidth, fillHeight, this.cornerRadius);
    this.fillBar.fill(thermometerGradient);

    // Glossy highlight: white fade on left side for glass effect
    const glossGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0.5 },
      end: { x: 1, y: 0.5 },
      colorStops: [
        { offset: 0, color: 'rgba(255,255,255,0.35)' },
        { offset: 0.5, color: 'rgba(255,255,255,0.1)' },
        { offset: 1, color: 'rgba(255,255,255,0)' },
      ],
      textureSpace: 'local',
    });

    this.glossOverlay.roundRect(offsetX, fillY, this.barWidth, fillHeight, this.cornerRadius);
    this.glossOverlay.fill(glossGradient);
  }
}

// =============================================================================
// COLOR INTERPOLATION
// =============================================================================

/**
 * Build gradient color stops for the thermometer fill.
 * Maps the relevant portion of COLOR_STOPS into the 0-1 range of the fill shape.
 * Bottom of fill = red (weakest), top = current strength color.
 */
function buildGradientStops(fillPercent: number): { offset: number; color: string }[] {
  const stops: { offset: number; color: string }[] = [];

  // Always start with red at the bottom (offset 1 = bottom in our gradient)
  stops.push({ offset: 0, color: hexToString(COLOR_STOPS[0]![1]) });

  // Add intermediate color stops that fall within the fill range
  for (const [position, color] of COLOR_STOPS) {
    if (position > 0 && position < fillPercent) {
      // Remap position from [0, fillPercent] to [0, 1]
      const remapped = position / fillPercent;
      stops.push({ offset: remapped, color: hexToString(color) });
    }
  }

  // End with the exact color at the top
  stops.push({ offset: 1, color: hexToString(interpolateColor(fillPercent)) });

  return stops;
}

/** Convert a hex number to a CSS color string */
function hexToString(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/** Lighten a hex color by blending towards white. Factor 0-1. */
function lightenColor(color: number, factor: number): number {
  return lerpColor(color, 0xffffff, factor);
}

/** Darken a hex color by blending towards black. Factor 0-1. */
function darkenColor(color: number, factor: number): number {
  return lerpColor(color, 0x000000, factor);
}

/**
 * Interpolate between color stops based on a 0-1 position.
 * Returns a single hex color value.
 */
function interpolateColor(position: number): number {
  const clamped = Math.max(0, Math.min(1, position));

  // Find the two color stops to interpolate between
  let lowerStop = COLOR_STOPS[0]!;
  let upperStop = COLOR_STOPS[COLOR_STOPS.length - 1]!;

  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    if (clamped >= COLOR_STOPS[i]![0] && clamped <= COLOR_STOPS[i + 1]![0]) {
      lowerStop = COLOR_STOPS[i]!;
      upperStop = COLOR_STOPS[i + 1]!;
      break;
    }
  }

  // Calculate interpolation factor within this segment
  const range = upperStop[0] - lowerStop[0];
  const factor = range === 0 ? 0 : (clamped - lowerStop[0]) / range;

  return lerpColor(lowerStop[1], upperStop[1], factor);
}

/** Linear interpolation between two hex colors */
function lerpColor(colorA: number, colorB: number, factor: number): number {
  const rA = (colorA >> 16) & 0xff;
  const gA = (colorA >> 8) & 0xff;
  const bA = colorA & 0xff;

  const rB = (colorB >> 16) & 0xff;
  const gB = (colorB >> 8) & 0xff;
  const bB = colorB & 0xff;

  const r = Math.round(rA + (rB - rA) * factor);
  const g = Math.round(gA + (gB - gA) * factor);
  const b = Math.round(bA + (bB - bA) * factor);

  return (r << 16) | (g << 8) | b;
}
